/**
 * Finance accounts — grid/table of accounts with computed balances, type and
 * group badges, owner label, and last-transaction recency hints (>30d amber,
 * >60d red). Create/edit via a FormModal; archive with a confirm dialog that
 * surfaces the 409 `account_has_transactions` case clearly.
 */

import { useMemo, useState } from 'react';
import {
  useFinanceAccounts,
  useCreateFinanceAccount,
  usePatchFinanceAccount,
  useArchiveFinanceAccount,
  type FinanceAccountDto,
  type FinanceAccountInput,
} from '../../api/finance';
import { ApiError } from '../../api/client';
import { FormModal } from '../../components/FormModal';
import { ConfirmDialog } from '../../components/finance/ConfirmDialog';
import {
  ACCOUNT_TYPE_LABELS,
  ACCOUNT_GROUP_LABELS,
  formatEur,
  formatNative,
  staleHint,
  financeErrorCode,
  financeErrorMessage,
} from './finance-format';
import formStyles from '../../components/FormModal.module.css';
import styles from './finance.module.css';

export function FinanceAccountsScreen() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const accountsQ = useFinanceAccounts({ includeArchived });
  const accounts = useMemo(() => accountsQ.data ?? [], [accountsQ.data]);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<FinanceAccountDto | null>(null);
  const [parentForNew, setParentForNew] = useState<FinanceAccountDto | null>(null);
  const [archiving, setArchiving] = useState<FinanceAccountDto | null>(null);

  const openCreate = () => {
    setEditing(null);
    setParentForNew(null);
    setFormOpen(true);
  };
  const openSubAccount = (parent: FinanceAccountDto) => {
    setEditing(null);
    setParentForNew(parent);
    setFormOpen(true);
  };
  const openEdit = (a: FinanceAccountDto) => {
    setEditing(a);
    setParentForNew(null);
    setFormOpen(true);
  };

  // Order accounts so each top-level account is followed by its sub-accounts.
  // Orphans (a child whose parent is filtered out) fall back to the top level.
  const ordered = useMemo(() => {
    const out: { account: FinanceAccountDto; depth: number }[] = [];
    const placed = new Set<string>();
    const childrenOf = (id: string) => accounts.filter((a) => a.parentAccountId === id);
    for (const top of accounts.filter((a) => !a.parentAccountId)) {
      out.push({ account: top, depth: 0 });
      placed.add(top.id);
      for (const child of childrenOf(top.id)) {
        out.push({ account: child, depth: 1 });
        placed.add(child.id);
      }
    }
    for (const a of accounts) {
      if (!placed.has(a.id)) out.push({ account: a, depth: 0 });
    }
    return out;
  }, [accounts]);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>Accounts</h2>
          <div className={styles.subtitle}>
            Every account with its live balance (native and EUR). Stale accounts —
            no movement in 30+ days — are flagged so a missed import doesn&apos;t hide.
          </div>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.toggle} ${includeArchived ? styles.toggleOn : ''}`}
            onClick={() => setIncludeArchived((v) => !v)}
          >
            {includeArchived ? 'Hiding none' : 'Show archived'}
          </button>
          <button type="button" className="kos-btn kos-btn-primary" onClick={openCreate}>
            + New account
          </button>
        </div>
      </header>

      {accountsQ.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : accountsQ.isError ? (
        <div className={styles.error}>{financeErrorMessage(accountsQ.error)}</div>
      ) : accounts.length === 0 ? (
        <div className={styles.empty}>
          No accounts yet. Create your first with the button above.
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Account</th>
                <th>Type</th>
                <th>Group</th>
                <th>Owner</th>
                <th style={{ textAlign: 'right' }}>Balance</th>
                <th>Last movement</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {ordered.map(({ account: a, depth }) => {
                const stale = staleHint(a.lastTransactionDate);
                const isEur = a.currency === 'EUR';
                return (
                  <tr key={a.id} className={a.archivedAt ? styles.archived : undefined}>
                    <td>
                      <span
                        className={styles.rowName}
                        style={depth > 0 ? { paddingLeft: 22 } : undefined}
                      >
                        {depth > 0 && <span aria-hidden style={{ opacity: 0.5 }}>↳ </span>}
                        {a.name}
                      </span>
                      {depth > 0 && (
                        <span className={styles.badge} style={{ marginLeft: 8 }}>
                          sub-account
                        </span>
                      )}
                      {a.archivedAt && (
                        <span className={styles.badge} style={{ marginLeft: 8 }}>
                          archived
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={styles.badge}>{ACCOUNT_TYPE_LABELS[a.type]}</span>
                    </td>
                    <td>
                      <span
                        className={`${styles.badge} ${
                          a.group === 'asset' ? styles.badgeAsset : styles.badgeLiability
                        }`}
                      >
                        {ACCOUNT_GROUP_LABELS[a.group]}
                      </span>
                    </td>
                    <td>
                      <span className={styles.owner}>{a.ownerLabel || '—'}</span>
                    </td>
                    <td className={styles.num}>
                      <div>{formatEur(a.currentBalanceEur)}</div>
                      {!isEur && (
                        <div className={styles.numSub}>
                          {formatNative(a.currentBalance, a.currency)}
                        </div>
                      )}
                    </td>
                    <td>
                      <span
                        className={`${styles.stale} ${
                          stale.level === 'danger'
                            ? styles.staleDanger
                            : stale.level === 'warn'
                              ? styles.staleWarn
                              : ''
                        }`}
                        title={a.lastTransactionDate ?? 'No transactions yet'}
                      >
                        {stale.label}
                      </span>
                    </td>
                    <td>
                      <div className={styles.rowActions}>
                        <button
                          type="button"
                          className={styles.iconBtn}
                          onClick={() => openEdit(a)}
                        >
                          Edit
                        </button>
                        {depth === 0 && !a.archivedAt && (
                          <button
                            type="button"
                            className={styles.iconBtn}
                            onClick={() => openSubAccount(a)}
                          >
                            + Sub-account
                          </button>
                        )}
                        {!a.archivedAt && (
                          <button
                            type="button"
                            className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                            onClick={() => setArchiving(a)}
                          >
                            Archive
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AccountFormModal
        open={formOpen}
        account={editing}
        parent={parentForNew}
        onClose={() => setFormOpen(false)}
      />
      <ArchiveAccountDialog
        account={archiving}
        onClose={() => setArchiving(null)}
      />
    </div>
  );
}

// ─── Create / edit modal ───────────────────────────────────────────────────

const TYPE_OPTIONS: FinanceAccountInput['type'][] = [
  'checking',
  'savings',
  'credit-card',
  'investment',
  'loan',
  'cash',
];

function AccountFormModal({
  open,
  account,
  parent,
  onClose,
}: {
  open: boolean;
  account: FinanceAccountDto | null;
  /** When set (and not editing), the new account is created as a child of this one. */
  parent: FinanceAccountDto | null;
  onClose: () => void;
}) {
  const create = useCreateFinanceAccount();
  const patch = usePatchFinanceAccount();
  const isEdit = !!account;
  const isChild = !isEdit && !!parent;

  // Local form state, keyed by the account id so it resets when the target
  // changes (open with a different account / switch create↔edit).
  const [name, setName] = useState('');
  const [type, setType] = useState<FinanceAccountInput['type']>('checking');
  const [group, setGroup] = useState<FinanceAccountInput['group']>('asset');
  const [currency, setCurrency] = useState('EUR');
  const [ownerLabel, setOwnerLabel] = useState('');
  const [initialBalance, setInitialBalance] = useState('0');
  const [initialBalanceDate, setInitialBalanceDate] = useState('');
  const [formKey, setFormKey] = useState<string | null>(null);

  // Sync local state to the active target on (re)open.
  const targetKey = open ? (account?.id ?? (parent ? `new:${parent.id}` : 'new')) : null;
  if (open && targetKey !== formKey) {
    setFormKey(targetKey);
    setName(account?.name ?? '');
    setType(account?.type ?? 'checking');
    setGroup(account?.group ?? parent?.group ?? 'asset');
    setCurrency(account?.currency ?? parent?.currency ?? 'EUR');
    setOwnerLabel(account?.ownerLabel ?? '');
    setInitialBalance(account ? String(account.initialBalance) : '0');
    setInitialBalanceDate(account?.initialBalanceDate ?? '');
    create.reset();
    patch.reset();
  }

  const pending = create.isPending || patch.isPending;
  const trimmedName = name.trim();
  const balNum = Number(initialBalance);
  const valid =
    trimmedName.length > 0 &&
    currency.trim().length === 3 &&
    Number.isFinite(balNum);

  const error = financeErrorMessage(create.error ?? patch.error);

  const submit = () => {
    if (!valid || pending) return;
    const payload: FinanceAccountInput = {
      name: trimmedName,
      type,
      group,
      currency: currency.trim().toUpperCase(),
      ownerLabel: ownerLabel.trim(),
      initialBalance: balNum,
      initialBalanceDate: initialBalanceDate || null,
      ...(isChild && parent ? { parentAccountId: parent.id } : {}),
    };
    if (isEdit && account) {
      patch.mutate({ id: account.id, patch: payload }, { onSuccess: onClose });
    } else {
      create.mutate(payload, { onSuccess: onClose });
    }
  };

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit account' : isChild ? `New sub-account of “${parent!.name}”` : 'New account'}
      subtitle={
        isChild
          ? `A linked sub-account of “${parent!.name}”. Inherits its currency (${parent!.currency}); you can move transactions between them.`
          : 'Name, type, group, currency, and an opening balance.'
      }
      submitLabel={pending ? 'Saving…' : 'Save account'}
      submitDisabled={!valid || pending}
      onSubmit={submit}
      error={error}
    >
      <div className={formStyles.field}>
        <label className={formStyles.fieldLabel} htmlFor="acc-name">
          Name
        </label>
        <input
          id="acc-name"
          className={formStyles.input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Family core · Millennium"
          maxLength={120}
        />
      </div>

      <div className={formStyles.field}>
        <div className={formStyles.row}>
          <div>
            <label className={formStyles.fieldLabel} htmlFor="acc-type">
              Type
            </label>
            <select
              id="acc-type"
              className={formStyles.select}
              value={type}
              onChange={(e) => setType(e.target.value as FinanceAccountInput['type'])}
            >
              {TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {ACCOUNT_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={formStyles.fieldLabel} htmlFor="acc-group">
              Group
            </label>
            <select
              id="acc-group"
              className={formStyles.select}
              value={group}
              onChange={(e) => setGroup(e.target.value as FinanceAccountInput['group'])}
            >
              <option value="asset">Asset</option>
              <option value="liability">Liability</option>
            </select>
          </div>
        </div>
      </div>

      <div className={formStyles.field}>
        <div className={formStyles.row}>
          <div>
            <label className={formStyles.fieldLabel} htmlFor="acc-currency">
              Currency
            </label>
            <input
              id="acc-currency"
              className={formStyles.input}
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              maxLength={3}
              placeholder="EUR"
              disabled={isChild}
              title={isChild ? 'Inherited from the parent account' : undefined}
            />
          </div>
          <div>
            <label className={formStyles.fieldLabel} htmlFor="acc-owner">
              Owner label
            </label>
            <input
              id="acc-owner"
              className={formStyles.input}
              value={ownerLabel}
              onChange={(e) => setOwnerLabel(e.target.value)}
              maxLength={40}
              placeholder="JC, TC, joint…"
            />
          </div>
        </div>
      </div>

      <div className={formStyles.field}>
        <div className={formStyles.row}>
          <div>
            <label className={formStyles.fieldLabel} htmlFor="acc-balance">
              Opening balance
            </label>
            <input
              id="acc-balance"
              className={formStyles.input}
              value={initialBalance}
              inputMode="decimal"
              onChange={(e) => setInitialBalance(e.target.value)}
            />
          </div>
          <div>
            <label className={formStyles.fieldLabel} htmlFor="acc-balance-date">
              As of date
            </label>
            <input
              id="acc-balance-date"
              className={formStyles.input}
              type="date"
              value={initialBalanceDate}
              onChange={(e) => setInitialBalanceDate(e.target.value)}
            />
          </div>
        </div>
        <span className={formStyles.fieldHint}>
          Balances are reported in EUR; non-EUR amounts use each transaction&apos;s
          stored EUR value.
        </span>
      </div>
    </FormModal>
  );
}

// ─── Archive confirm ─────────────────────────────────────────────────────────

function ArchiveAccountDialog({
  account,
  onClose,
}: {
  account: FinanceAccountDto | null;
  onClose: () => void;
}) {
  const archive = useArchiveFinanceAccount();
  const open = !!account;

  // Reset mutation state when the target changes.
  const [seen, setSeen] = useState<string | null>(null);
  if (open && account.id !== seen) {
    setSeen(account.id);
    archive.reset();
  }
  if (!open && seen !== null) setSeen(null);

  const err = archive.error;
  const conflict =
    err instanceof ApiError && err.status === 409 && financeErrorCode(err) === 'account_has_transactions';

  const accountName = account?.name ?? '';
  const message = conflict
    ? `“${accountName}” still has transactions, so it can’t be archived. Move or remove its transactions first, then try again.`
    : `Archive “${accountName}”? It stays in reports and history — it just drops out of active account pickers. You can restore it later.`;

  return (
    <ConfirmDialog
      open={open}
      title={conflict ? 'Cannot archive yet' : 'Archive account'}
      message={message}
      confirmLabel={archive.isPending ? 'Archiving…' : 'Archive'}
      confirmDisabled={archive.isPending || conflict}
      destructive
      hideConfirm={conflict}
      error={!conflict ? financeErrorMessage(err) : null}
      onClose={onClose}
      onConfirm={() => {
        if (!account) return;
        archive.mutate(account.id, { onSuccess: onClose });
      }}
    />
  );
}

